import React, { useEffect, useRef, useState } from "react";
import Skeleton from "../../components/Skeleton";

const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setSize({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height,
        });
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
};

export default function DashboardCard({
  title,
  value,
  subInfoIcon,
  subInfoText,
  mainIcon,
  onClick,
  bgColor,
  isEditMode,
  trend,
  isMobile,
  GlassCardComponent,
  birthdayFallbackIcon,
  ...props
}) {
  const [cardRef, { height }] = useElementSize();
  const iconSize = Math.max(22, Math.min(60, height * 0.25));
  const valueSize = Math.max(14, Math.min(48, height * 0.19));
  const titleSize = Math.max(10, Math.min(16, height * 0.08));
  const subInfoSize = Math.max(9, Math.min(14, height * 0.065));
  const padding = Math.max(8, Math.min(18, height * 0.07));
  const GlassCard = GlassCardComponent;

  return (
    <GlassCard
      ref={cardRef}
      onClick={onClick}
      isEditMode={isEditMode}
      variant={isMobile ? "solid" : "glass"}
      {...props}
      className={`flex flex-col justify-center items-center text-center h-full group relative overflow-hidden ${isMobile ? "!rounded-[20px]" : ""}`}
      style={{ padding: `${padding}px` }}
    >
      {value === "loading" ? (
        <div className="flex flex-col items-center gap-4 w-full h-full justify-center">
          <Skeleton className="rounded-2xl" style={{ width: iconSize, height: iconSize }} />
          <Skeleton className="w-16 h-4 rounded-full" />
          <Skeleton className="w-24 h-8 rounded-lg" />
        </div>
      ) : (
        <>
          {!isMobile && <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-20 blur-3xl ${bgColor} group-hover:scale-125 transition-transform duration-700`}></div>}

          <div className="relative z-10 flex flex-col items-center w-full" style={{ gap: isMobile ? "10px" : `${height * 0.035}px` }}>
            <div className={`rounded-2xl shadow-sm border backdrop-blur-md transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 ${isMobile ? "bg-gray-50 border-gray-100" : "bg-white/60 border-white/50"}`} style={{ padding: `${height * 0.04}px` }}>
              <img src={mainIcon} width={iconSize} alt="icon" className="drop-shadow-md" style={{ width: `${iconSize}px`, height: `${iconSize}px` }} />
            </div>

            <div className="flex flex-col items-center">
              <h3 className="text-gray-500 font-bold tracking-wide" style={{ fontSize: `${titleSize}px` }}>{title}</h3>
              <div className="flex items-end gap-2">
                <span className="font-black text-[#2C3E30] font-do-hyeon drop-shadow-sm" style={{ fontSize: `${valueSize}px`, lineHeight: 1.1 }}>{value}</span>
                {trend !== undefined && (
                  <span className={`font-bold mb-1.5 ${trend > 0 ? "text-red-500" : trend < 0 ? "text-blue-500" : "text-gray-400"}`} style={{ fontSize: `${valueSize * 0.4}px` }}>
                    {trend === 0 ? "-" : `${trend > 0 ? "▲" : "▼"} ${Math.abs(trend)}`}
                  </span>
                )}
              </div>
            </div>

            <div className={`flex items-center gap-1.5 rounded-full border shadow-sm ${isMobile ? "bg-gray-50 border-gray-100" : "bg-white/50 border-white/60"}`} style={{ padding: `${height * 0.015}px ${height * 0.05}px` }}>
              <img
                src={subInfoIcon}
                width={subInfoSize * 1.4}
                alt="status"
                className={subInfoIcon?.startsWith("http") && !subInfoIcon.includes("Animated-Fluent-Emojis") ? "rounded-full object-cover" : ""}
                style={{ width: `${subInfoSize * 1.4}px`, height: `${subInfoSize * 1.4}px` }}
                onError={(e) => {
                  if (birthdayFallbackIcon) {
                    e.target.src = birthdayFallbackIcon;
                    e.target.className = "";
                  }
                }}
              />
              <span className="font-bold text-gray-600" style={{ fontSize: `${subInfoSize}px` }}>{subInfoText}</span>
            </div>
          </div>
        </>
      )}
    </GlassCard>
  );
}
